import { useEffect, useRef } from 'react';
import Button from './Button';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  variant: 'alert' | 'confirm';
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  onConfirm?: () => void;
}

export default function Modal({
  open,
  onClose,
  title,
  message,
  variant,
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
  onConfirm,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleConfirm = () => {
    onConfirm?.();
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby="modal-message"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-2xl border border-neutral-border bg-white shadow-modal p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h2 id="modal-title" className="text-lg font-semibold text-neutral-primary mb-2">
            {title}
          </h2>
        )}
        <p id="modal-message" className="text-sm text-neutral-secondary mb-6">
          {message}
        </p>
        <div className="flex justify-end gap-2">
          {variant === 'confirm' && (
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            variant={variant === 'alert' ? 'primary' : confirmVariant}
            onClick={variant === 'alert' ? onClose : handleConfirm}
          >
            {variant === 'alert' ? 'OK' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
